import React, { useMemo } from "react";
import { useAppearance } from "../../context/AppearanceContext";

// Fixed, non-interactive decorative layer. Particles are lightweight CSS-animated spans, paused by
// the browser when the tab is hidden. Fully suppressed by reduced motion, the ambient toggle, or
// the pl-no-ambient / pl-reduce-motion classes (see index.css). Never blocks interaction (z-index
// below chrome, pointer-events: none).
const GLYPHS = {
  snow: "❄", leaves: "🍂", petals: "🌸", sugar: "•", clouds: "☁", ghosts: "👻", shimmer: "✦",
};
const ANIM = {
  snow: "pl-snowfall", leaves: "pl-leaffall", petals: "pl-leaffall", sugar: "pl-snowfall",
  clouds: "pl-drift", ghosts: "pl-floatup", shimmer: "pl-twinkle",
};

function rand(seed) { const x = Math.sin(seed * 9973.13) * 43758.5453; return x - Math.floor(x); }

export default function SeasonalAmbient() {
  const { theme, prefs } = useAppearance();
  const cfg = theme?.ambient;
  const particles = useMemo(() => {
    if (!cfg) return [];
    const count = Math.min(cfg.density || 20, 72);
    return Array.from({ length: count }, (_, i) => {
      const left = Math.round(rand(i + 1) * 100);
      const big = cfg.type === "snow" || cfg.type === "leaves";
      const size = (big ? 11 : 8) + Math.round(rand(i + 7) * (big ? 20 : 14));
      const dur = 9 + Math.round(rand(i + 3) * 14);
      const delay = -Math.round(rand(i + 5) * dur);
      const drift = `${Math.round((rand(i + 11) - 0.5) * 120)}px`;
      const opacity = 0.5 + rand(i + 13) * 0.5;
      return { i, left, size, dur, delay, drift, opacity };
    });
  }, [cfg]);

  if (!cfg || !prefs.ambient || prefs.reduceMotion) return null;
  const anim = ANIM[cfg.type] || "pl-snowfall";
  const glyph = GLYPHS[cfg.type] || "❄";
  const isTwinkle = cfg.type === "shimmer";

  return (
    <div className="pl-ambient" aria-hidden="true" data-testid="seasonal-ambient" data-effect={cfg.type}>
      {particles.map((p) => (
        <span key={p.i} className="pl-particle" style={{
          left: `${p.left}%`,
          fontSize: `${p.size}px`,
          color: cfg.colour,
          opacity: p.opacity,
          "--pl-drift": p.drift,
          animation: `${anim} ${p.dur}s linear ${p.delay}s infinite`,
          ...(isTwinkle ? { top: `${Math.round(rand(p.i + 21) * 90)}%` } : {}),
        }}>{glyph}</span>
      ))}
    </div>
  );
}
